import { computed, effect, inject, Injectable, resource, untracked } from '@angular/core';

import { GatewayId, GatewayListItem } from '../site-gateway/models/gateway';
import { GatewayDetailVm, GatewayOverviewVm } from '../site-gateway/models/gateway-vm';

import { CurrentSiteStore } from './current-site.store';
import { DeviceId, DeviceMS } from './models/Device';
import { SetupId, SiteId } from './models/Site';
import { SiteDevicesGateway } from './site-devices.gateway';
import { buildDeviceFromRawDevice, getGatewayDevices, normalizeRawDevice } from './utils/devices';
import {
    buildGatewayDetailVm,
    buildGatewayOverviewVm,
    isGatewayAvailable,
    sortGatewaysByMainProtocol,
} from './utils/gateway';

interface SiteKey {
    siteId: SiteId;
    setupId: SetupId | null;
}

/**
 * The boxes and devices of the current installation, kept up to date by the real-time events.
 * - Loaded when the installation changes, and only then: coming back to the same installation does not reload. The
 *   boxes and the devices load side by side, so the boxes do not wait for the slower device synchronization.
 * - A box list that failed to load is asked again by `retryGateways`, which the installation page calls when it opens.
 * - An event about another installation, or received before the devices are loaded, is ignored: that load returns the
 *   fresh list.
 * - The devices are read again, as the backend recorded them, each time the backend confirms the socket follows the
 *   installation: a change made before that confirmation reached no socket.
 * - The state lives as long as the page: logging out leaves the application, so there is no session reset.
 */
@Injectable({ providedIn: 'root' })
export class SiteDevicesStore {
    private readonly siteDevicesGateway = inject(SiteDevicesGateway);
    private readonly currentSite = inject(CurrentSiteStore);
    private readonly site = this.currentSite.site;
    // A confirmation received before this store existed came before its devices load, which sees the changes.
    private servedJoinConfirmation = untracked(this.currentSite.lastJoinConfirmation);

    private readonly key = computed<SiteKey | undefined>(
        () => {
            const site = this.site();
            return site ? { siteId: site.id, setupId: site.setupId } : undefined;
        },
        { equal: (a, b) => a?.siteId === b?.siteId && a?.setupId === b?.setupId },
    );
    // A failure stays an error rather than an empty list, so that it can be asked again.
    private readonly boxList = resource({
        params: () => this.key(),
        loader: ({ params: { setupId }, abortSignal }): Promise<GatewayListItem[]> =>
            setupId ? this.siteDevicesGateway.getGateways(setupId, abortSignal) : Promise.resolve([]),
    });
    private readonly deviceList = resource({
        params: () => this.key()?.siteId,
        loader: ({ params, abortSignal }) =>
            this.siteDevicesGateway.getDevices(params, abortSignal).then(
                (devices) => devices.map(normalizeRawDevice),
                (): DeviceMS[] => [],
            ),
    });

    constructor() {
        effect(() => {
            const confirmation = this.currentSite.lastJoinConfirmation();
            if (!confirmation || confirmation === this.servedJoinConfirmation) return;
            if (!this.deviceList.hasValue() || this.deviceList.isLoading()) return;

            this.servedJoinConfirmation = confirmation;
            untracked(() => void this.readRecordedDevices(confirmation.siteId));
        });
    }

    /** True once the devices are known and the box list has settled, even on a failure. */
    readonly loaded = computed(() => this.deviceList.hasValue() && !this.boxList.isLoading());
    readonly devices = computed<DeviceMS[]>(() => (this.deviceList.hasValue() ? this.deviceList.value() : []));
    private readonly siteGateways = computed(() => (this.boxList.hasValue() ? this.boxList.value() : []));

    /** The box tabs, the boxes holding io or zigbee devices first. */
    readonly gateways = computed(() => sortGatewaysByMainProtocol(this.siteGateways(), this.devices()), {
        equal: sameItems,
    });

    readonly gatewayDetails = computed<GatewayDetailVm[]>(() =>
        this.siteGateways()
            .map(({ gatewayId }) => this.gatewayDevice(gatewayId))
            .filter((device) => device !== undefined)
            .map((device) => buildGatewayDetailVm(buildDeviceFromRawDevice(device, device.id))),
    );

    /** The devices paired to that box, without the box itself. */
    gatewayDevices(gatewayId: GatewayId | null): DeviceMS[] {
        return getGatewayDevices(this.devices(), gatewayId);
    }

    /** The device standing for that box, if it is one of the installation's boxes. */
    gatewayDevice(gatewayId: GatewayId | null): DeviceMS | undefined {
        if (!this.siteGateways().some((gateway) => gateway.gatewayId === gatewayId)) return undefined;

        return this.devices().find((device) => device.deviceurl === gatewayId);
    }

    gateway(gatewayId: GatewayId | null): GatewayOverviewVm | null {
        const device = this.gatewayDevice(gatewayId);
        return device ? buildGatewayOverviewVm(buildDeviceFromRawDevice(device, device.id)) : null;
    }

    isGatewayAvailable(gatewayId: GatewayId | null | undefined): boolean {
        return isGatewayAvailable(this.siteGateways(), gatewayId);
    }

    retryGateways(): void {
        if (this.boxList.status() === 'error') this.boxList.reload();
    }

    deviceCreated(siteId: SiteId, device: DeviceMS): void {
        this.change(siteId, (devices) =>
            devices.some(({ id }) => id === device.id) ? null : [...devices, normalizeRawDevice(device)],
        );
    }

    /** Replaces a device already known, with its latest version from the backend. */
    deviceUpdated(siteId: SiteId, device: DeviceMS): void {
        this.change(siteId, (devices) => replace(devices, device.id, () => normalizeRawDevice(device)));
    }

    deviceRenamed(siteId: SiteId, deviceId: DeviceId, name: string): void {
        this.change(siteId, (devices) => replace(devices, deviceId, (device) => ({ ...device, name })));
    }

    deviceRemoved(siteId: SiteId, deviceId: DeviceId): void {
        this.change(siteId, (devices) =>
            devices.some(({ id }) => id === deviceId) ? devices.filter(({ id }) => id !== deviceId) : null,
        );
    }

    /** `update` returns null when nothing changes. */
    private change(siteId: SiteId, update: (devices: DeviceMS[]) => DeviceMS[] | null): void {
        if (siteId !== this.key()?.siteId || !this.deviceList.hasValue()) return;

        const devices = update(this.deviceList.value());
        if (devices) this.deviceList.set(devices);
    }

    private async readRecordedDevices(siteId: SiteId): Promise<void> {
        try {
            const devices = (await this.siteDevicesGateway.getRecordedDevices(siteId)).map(normalizeRawDevice);
            if (this.isShowing(siteId)) this.deviceList.set(devices);
        } catch {
            // The list shown stays.
        }
    }

    private isShowing(siteId: SiteId): boolean {
        return siteId === this.key()?.siteId && this.deviceList.hasValue() && !this.deviceList.isLoading();
    }
}

function replace(devices: DeviceMS[], deviceId: DeviceId, build: (device: DeviceMS) => DeviceMS): DeviceMS[] | null {
    return devices.some(({ id }) => id === deviceId)
        ? devices.map((device) => (device.id === deviceId ? build(device) : device))
        : null;
}

function sameItems<T>(a: readonly T[], b: readonly T[]): boolean {
    return a.length === b.length && a.every((item, index) => item === b[index]);
}
